import { PropTypes } from 'typed/imports';
import { ComponentEventHandler1, PureComponent, ReactEventHandler } from 'typed/components/utils';
import { ComponentElement, ReactElement, ReactNode } from 'react';
import { default as FormattedResource, Resource, isResource, resourcePropType } from '../FormattedResource/FormattedResource';
import { toArray } from 'typed/utils';
import DropPanel, { CloseMode, SnapSide } from 'components/common/DropPanel/DropPanel';
import './Menu.less';

interface GroupProps {
    title: ReactElement | Resource;
    className?: string;
    children?: GroupChildren;
}

export class Group extends PureComponent<GroupProps> {
    static readonly displayName = 'Menu.Group';

    static readonly propTypes = {
        title: PropTypes.oneOfType([resourcePropType, PropTypes.element]),
        className: PropTypes.string
    };

    static readonly defaultProps = {
        className: ''
    };

    render(): ReactElement {
        const { title, className, children } = this.props;

        return (
            <div className={`group ${className}`}>
                <div className="title">{isResource(title) ? <FormattedResource resource={title} /> : title}</div>
                <div className="items">{children}</div>
            </div>
        );
    }
}

interface ItemProps {
    icon?: string;
    title: ReactElement | Resource;
    checked?: boolean;
    disabled?: boolean;
    className?: string;
    onClick?: ComponentEventHandler1<unknown>;
    children?: never;
}

export class Item extends PureComponent<ItemProps> {
    static readonly displayName = 'Menu.Item';

    static readonly propTypes = {
        title: PropTypes.oneOfType([resourcePropType, PropTypes.element]).isRequired,
        checked: PropTypes.bool,
        disabled: PropTypes.bool,
        className: PropTypes.string
    };

    static readonly defaultProps = {
        className: ''
    };

    private _onClick: ReactEventHandler = (event) => {
        if (this.props.onClick) {
            this.props.onClick(this, event);
        }
    };

    render(): ReactElement {
        const { icon, title, checked, disabled, className } = this.props;

        return (
            <div
                className={`item ${className}${checked ? ' checked' : ''}${disabled ? ' disabled' : ''}`}
                onClick={!disabled ? this._onClick : undefined}>
                <span className="title">
                    {icon ? <span className={`fa fa-${icon}`} /> : null}
                    {checked ? <span className="fa fa-check" /> : null}
                    {isResource(title) ? <FormattedResource resource={title} /> : title}
                </span>
            </div>
        );
    }
}

type GroupChild =
    | undefined | null
    | ComponentElement<ItemProps, Item>
    | ReactElement /* TODO validate children at compile time? for some reason `<Item />` has type of Element, with no information of it's origin */;

type GroupChildren = GroupChild | GroupChild[] | (GroupChild | GroupChild[])[];

type Child =
    | ComponentElement<GroupProps, Group>
    | GroupChild;

type Children = Child | Child[] | (Child | Child[])[];

const
    isImpossibleChild = (_child: ReactElement): _child is ReactElement => false,
    isGroup = (child: ReactElement): child is ReactElement<GroupProps> => !!child && typeof child === 'object' && child.type === Group,
    isItem = (child: ReactElement): child is ReactElement<ItemProps> => !!child && typeof child === 'object' && child.type === Item;

function keyify(child: Child, groupKey: string): { key: string; child: Child; };
function keyify(child: Child[], groupKey: string): { key: string; child: Child; }[];
function keyify(child: Child | Child[], groupKey: string): { key: string; child: Child; } | { key: string; child: Child; }[] {
    return Array.isArray(child) ?
        child.map((subchild, subindex) => keyify(subchild, `${groupKey}.${subindex}`)) :
        {
            key: child?.key ? `external-${child.key}` : `index-${groupKey}`,
            child
        };
}

interface Props /* extends DropPanelProps */ {
    snap?: typeof DropPanel.SnapSide | (typeof DropPanel.SnapSide)[];
    arrowed?: boolean;
    closeMode?: typeof DropPanel.CloseMode | (typeof DropPanel.CloseMode)[];
    title?: ReactElement | Resource | null;
    icon?: ReactElement | Resource | null;
    className?: string;
    disabled?: boolean;
    opened?: boolean;
    onClick?: ComponentEventHandler1<unknown>;
    onOpen?: ComponentEventHandler1<unknown>;
    onClose?: ComponentEventHandler1<unknown>;
    children?: Children;
}

interface State {
    opened: boolean;
}

export default class Menu extends PureComponent<Props, State> {
    static readonly Group = Group;
    static readonly Item = Item;

    static get SnapSide() { return SnapSide; }
    static get CloseMode() { return CloseMode; }

    static get propTypes() { return DropPanel.propTypes; } // no need for getter, but direct assignment causes some circular dependencies

    static get defaultProps() { // should probably work once DropPanel is moved to TypeScript section
        return {
            ...DropPanel.defaultProps,
            arrowed: true,
            snap: [SnapSide.RIGHT],
            closeMode: CloseMode.OUTSIDE
        };
    }

    state: State = { opened: false };

    componentWillMount() {
        this.componentWillReceiveProps(this.props);
    }

    componentWillReceiveProps(nextProps: Props) {
        if (nextProps.opened !== undefined) {
            this.setState({ opened: nextProps.opened });
        }
    }

    private _onClick: ComponentEventHandler1<unknown> = (_sender, event) => {
        if (this.props.onClick) { this.props.onClick(this, event); }
    };

    private _onOpen: ComponentEventHandler1<unknown> = (_sender, event) => {
        this.setState({ opened: true });

        if (this.props.onOpen) {
            this.props.onOpen(this, event);
        }
    };

    private _onItemClick = (itemOnClick: ComponentEventHandler1<unknown> | undefined, _sender: unknown, event: unknown) => {
        if (itemOnClick) {
            itemOnClick(this, event);
        }

        if (this.props.closeMode === CloseMode.INSIDE || !this.props.closeMode || this.props.closeMode.includes(CloseMode.INSIDE)) {
            this._onClose(this, event);
        }
    };

    // TODO navigation with arrow keys

    private _onClose: ComponentEventHandler1<unknown> = (_sender, event) => {
        this.setState({ opened: false });

        if (this.props.onClose) {
            this.props.onClose(this, event);
        }
    };

    private _renderChildren(children: Children = this.props.children): ReactNode[] {
        return toArray(children || [])
            .map((child, index) => keyify(child as any as Child[], `${index}`))
            .flatten()
            .map(({ key, child }) => {
                if (!child || isImpossibleChild(child)) { return null; }

                if (isGroup(child)) {
                    return <Group key={key} {...child.props}>{this._renderChildren(child.props.children) as any}</Group>;
                }

                if (isItem(child)) {
                    // TODO is it possible to listen for Portal'ed items?
                    return <Item key={key} {...child.props} onClick={!child.props.disabled ? this._onItemClick.bind(this, child.props.onClick) : undefined} />;
                }

                return child;
            })
            .filter(v => v);
    }

    render() {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { disabled, className, onOpen, onClose, onClick, children, ...dpProps } = this.props;

        return (
            <DropPanel
                {...dpProps}
                disabled={disabled || !children || Array.isArray(children) && !children.some(v => v)}
                className={`menu ${className}`}
                opened={this.state.opened}
                onClick={this._onClick}
                onOpen={this._onOpen}
                onClose={this._onClose}
            >
                {this._renderChildren()}
            </DropPanel>
        );
    }
}
